import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import DateFilter, { DateInput, formatDisplayDate, isTodayFilter, todayDateFilter, toYMD } from './DateFilter';
import { usePagedQuery, dateRangeParams } from './usePagedQuery';
import { Pagination, SearchBox, DebouncedInput } from './TableControls';

// Timestamp (base36) + random suffix — large ID space so accidental collisions
// between two people creating tasks at the same time are very unlikely.
// The backend's UNIQUE constraint on tasks.display_id is still the real guarantee.
const generateTaskId = () => {
    const timePart = Date.now().toString(36).toUpperCase();
    const randPart = Math.floor(100 + Math.random() * 900);
    return `TSK-${timePart}${randPart}`;
};

const API = process.env.REACT_APP_BACKEND_API_URL;

const emptyForm = () => ({
    display_id: generateTaskId(),
    company: '',
    client_spoc: '',
    title: '',
    assigned_to: '',
    assigned_date: toYMD(new Date()),
    instructions: '',
    job_id: '',
    job_role: ''
});

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none'
};

const lockedInputStyle = { ...inputStyle, background: '#f3f4f6', color: '#6b7280' };
const filterInputStyle = { padding: '6px 9px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '11px', minWidth: '120px' };

const labelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
    display: 'block'
};

const fieldWrapStyle = { display: 'flex', flexDirection: 'column' };

const statusColors = {
    'Assigned To': { background: '#dbeafe', color: '#1e40af' },
    'Assigned': { background: '#dbeafe', color: '#1e40af' },
    'Pending': { background: '#e0e7ff', color: '#3730a3' },
    'Work In Progress': { background: '#fef3c7', color: '#92400e' },
    'ToSubmit': { background: '#fde68a', color: '#92400e' },
    'Submitted': { background: '#c7d2fe', color: '#3730a3' },
    'Completed': { background: '#dcfce7', color: '#166534' }
};

// refreshSharedTasks: optional — the App-level fetchTasks(), called after a
// successful create so the Tasks (Kanban) page reflects the new task too,
// not just this page's own table.
const CreatedTasksList = ({ user, onLogout, refreshSharedTasks }) => {
    const navigate = useNavigate();

    const [recruiters, setRecruiters] = useState([]);
    // Full, unfiltered job requirements — used for the task list's filter
    // dropdown options (a company/role stays filterable even once its job closes).
    const [allJobRequirements, setAllJobRequirements] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const { isMobile, isTablet } = useResponsive();

    // Item #2: only an Open job can ever be linked to a new task — this list
    // is what backs both the Company and Job Role selectors below. The
    // backend independently re-validates this on submit regardless.
    const openJobRequirements = allJobRequirements.filter(j => j.status === 'Open');

    // Company -> Job Role is a dependent selection: Job Role only ever lists
    // roles belonging to Open jobs at the currently-selected company, which
    // structurally prevents an invalid Company + Job Role combination.
    const companyOptions = Array.from(new Set(openJobRequirements.map(j => j.company_name).filter(Boolean))).sort();
    // A Job ID / Job Role can't be assigned again while its existing task is
    // not Completed: those jobs are kept out of the picker. activeJobTasks maps
    // job_id -> the blocking task (fetched fresh, covers tasks by other creators).
    const [activeJobTasks, setActiveJobTasks] = useState({});
    const fetchActiveJobTasks = useCallback(() => {
        fetch(`${API}/api/tasks/active-job-ids`)
            .then(res => res.json())
            .then(rows => {
                const map = {};
                (Array.isArray(rows) ? rows : []).forEach(r => { map[String(r.job_id).trim().toLowerCase()] = r; });
                setActiveJobTasks(map);
            })
            .catch(err => console.error('Failed to fetch active job tasks:', err));
    }, []);
    useEffect(() => { fetchActiveJobTasks(); }, [fetchActiveJobTasks]);
    const blockingTaskFor = (job) => (job ? activeJobTasks[String(job.job_id).trim().toLowerCase()] : null);

    const companyJobs = openJobRequirements.filter(j => j.company_name === form.company);
    const jobRoleOptionsForCompany = companyJobs.filter(j => !blockingTaskFor(j));
    const blockedJobsForCompany = companyJobs.filter(j => blockingTaskFor(j));

    // Task list filters (item #2's "searchable dropdowns" — applied to the
    // Created Tasks table, since Job ID/Req ID/Evanti SPOC are derived
    // job-level properties, not things picked while creating a task).
    // Item #3: Global Filters removed — Company Name (no SPOC) is now a plain,
    // page-specific filter, plus one combined Assigned/Reassigned Date filter.
    const [filterCompany, setFilterCompany] = useState('');
    const [dateFilter, setDateFilter] = useState(todayDateFilter);
    const [dateResetSignal, setDateResetSignal] = useState(0);
    const [filterJobRole, setFilterJobRole] = useState('');
    const [filterJobId, setFilterJobId] = useState('');
    const [filterReqId, setFilterReqId] = useState('');
    const [filterRecruiter, setFilterRecruiter] = useState('');
    const [filterEvantiSpoc, setFilterEvantiSpoc] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [search, setSearch] = useState('');

    // Server-side paging. Admin: one centralized list of everything created by ALL Admins and
    // Team Leads. Team Lead: their own. Newest task first; every filter below is applied by the API.
    const scopeParams = useMemo(() => (user
        ? (user.role === 'admin' ? { created_by_roles: 'admin,teamLead' } : { assigned_by: user.name })
        : {}), [user]);
    const taskParams = useMemo(() => {
        const r = dateRangeParams(dateFilter, 'assigned');
        return {
            ...scopeParams,
            search: search.trim(), status: filterStatus, company: filterCompany,
            job_role: filterJobRole.trim(), job_id: filterJobId.trim(), req_id: filterReqId.trim(),
            assigned_to: filterRecruiter.trim(), evanti_spoc: filterEvantiSpoc.trim(),
            assigned_from: r.assigned_from, assigned_to_date: r.assigned_to
        };
    }, [scopeParams, search, filterStatus, filterCompany, dateFilter, filterJobRole, filterJobId, filterReqId, filterRecruiter, filterEvantiSpoc]);
    const table = usePagedQuery(`${API}/api/tasks`, taskParams, { enabled: !!user });
    const tasks = table.rows;
    const loading = table.loading;
    const fetchCreatedTasks = table.refetch;

    // Dropdown / suggestion values come from the API (distinct values across ALL matching tasks, not just this page).
    const [filterOptions, setFilterOptions] = useState({});
    const loadFilterOptions = useCallback(() => {
        if (!user) return;
        fetch(`${API}/api/tasks/filter-options?${new URLSearchParams(scopeParams).toString()}`)
            .then(res => res.json())
            .then(data => setFilterOptions(data && !data.error ? data : {}))
            .catch(err => console.error('Failed to fetch filter options:', err));
    }, [user, scopeParams]);
    useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);

    // Item #6: who can be assigned depends on who's creating the task.
    // Team Lead -> Recruiters + self. Admin -> Team Leads + Recruiters.
    useEffect(() => {
        if (!user) return;

        let cancelled = false;

        const fetchAssignees = async () => {
            try {
                const response = await fetch(`${API}/api/users?roles=recruiter,teamLead`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch users: ${response.status}`);
                }

                const data = await response.json();

                if (cancelled) return;

                const list = (Array.isArray(data) ? data : []).filter(u => {
                    const role = u.role?.toLowerCase();

                    if (user.role === 'admin') {
                        return role === 'recruiter' || role === 'teamlead';
                    }

                    if (user.role === 'teamLead') {
                        return role === 'recruiter' || role === 'teamlead';
                    }

                    return false;
                });

                // Include the logged-in user even if not returned by the API.
                if (
                    user.role === 'teamLead' &&
                    user.name &&
                    !list.some(u => u.name?.toLowerCase() === user.name.toLowerCase())
                ) {
                    list.push({
                        id: user.id,
                        name: user.name,
                        role: 'teamLead'
                    });
                }

                setRecruiters(list);
            } catch (err) {
                console.error('Failed to fetch assignable users:', err);
            }
        };

        fetchAssignees();

        return () => {
            cancelled = true;
        };
    }, [user]);

    useEffect(() => {
        fetch(`${API}/api/job-requirements`)
            .then(res => res.json())
            .then(data => setAllJobRequirements(data))
            .catch(err => console.error('Failed to fetch job requirements:', err));
    }, []);

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    // Changing the company invalidates whatever Job Role/Client SPOC had
    // already been derived from the previous company's jobs.
    const handleCompanyChange = (e) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, company: value, job_role: '', job_id: '', client_spoc: '' }));
    };

    // Item #2: selecting a Job Role resolves to one specific Open job at the
    // selected company, and auto-fills Client SPOC + Assign Date (from that
    // job's Action Date) from it — both stay editable afterward.
    const handleJobRoleChange = (e) => {
        const selectedRole = e.target.value;
        const matchedJob = jobRoleOptionsForCompany.find(j => j.job_role === selectedRole);
        const blockedJob = !matchedJob ? blockedJobsForCompany.find(j => j.job_role === selectedRole) : null;
        if (blockedJob) {
            const b = blockingTaskFor(blockedJob);
            setError(`A task already exists for "${blockedJob.job_role}" (${blockedJob.job_id}, task ${b.display_id} — ${b.status}). It can be assigned again only after that task is marked Completed.`);
        } else {
            setError('');
        }
        setForm(prev => ({
            ...prev,
            job_role: selectedRole,
            job_id: matchedJob ? matchedJob.job_id : '',
            client_spoc: matchedJob?.client_spoc || prev.client_spoc,
            assigned_date: matchedJob?.action_date ? matchedJob.action_date.split('T')[0] : prev.assigned_date
        }));
    };

    const handleRegenerateId = () => {
        setForm(prev => ({ ...prev, display_id: generateTaskId() }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.company || !form.title || !form.assigned_to || !form.assigned_date || !form.job_role) {
            setError('Company, Job Role, Title, Assign To, and Assign Date are required.');
            return;
        }
        // Item #8: reject past dates client-side too — the <input min=...>
        // attribute blocks the calendar picker, but some browsers still allow
        // typing a past date directly, so this catches that. The backend is
        // still the real enforcement regardless.
        const todayStr = toYMD(new Date());
        if (form.assigned_date < todayStr) {
            setError('Task date cannot be in the past. Choose today or a future date.');
            return;
        }

        if (!form.job_id) {
            // Typed text that matched no available job (e.g. a role whose task isn't Completed yet).
            setError(`"${form.job_role}" is not an available Job Role for ${form.company}. Pick one from the list.`);
            return;
        }
        const selectedJob = companyJobs.find(j => j.job_id === form.job_id);
        const blocker = blockingTaskFor(selectedJob);
        if (blocker) {
            setError(`A task already exists for Job ID "${form.job_id}" (${blocker.display_id}, status: ${blocker.status}). A new task can only be created for this job after that task is marked Completed.`);
            return;
        }

        const { job_role, ...payloadFields } = form; // job_role was only needed client-side to derive job_id
        const payload = {
            ...payloadFields,
            assigned_by: user?.name || 'Team Lead',
            requester_role: user?.role
        };

        setSaving(true);
        try {
            const response = await fetch(`${API}/api/tasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (response.ok) {
                setForm(emptyForm());
                table.setPage(1);
                fetchCreatedTasks();
                loadFilterOptions();
                fetchActiveJobTasks();
                refreshSharedTasks?.(); // keep the Tasks (Kanban) page in sync too
                return;
            }

            if (response.status === 409 && data.code === 'DUPLICATE_JOB_TASK') {
                setError(data.error);
                fetchActiveJobTasks();
            } else if (response.status === 409) {
                const newId = generateTaskId();
                setForm(prev => ({ ...prev, display_id: newId }));
                setError(`That Task ID was already taken. Generated a new one (${newId}) — please review and click Create Task again.`);
            } else {
                setError(data.error || 'Failed to create task. Please try again.');
            }
        } catch (err) {
            console.error('Failed to create task:', err);
            setError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setSaving(false);
        }
    };

    // Task list filter options (from the API) and the rows of the current page.
    const taskFilterOptions = (field) => (filterOptions[field] || []);
    const filteredTasks = tasks;
    const hasTaskFilters = search.trim() || filterStatus || filterCompany || !isTodayFilter(dateFilter) || filterJobRole || filterJobId || filterReqId || filterRecruiter || filterEvantiSpoc;
    const clearTaskFilters = () => {
        setFilterCompany(''); setDateFilter(todayDateFilter());
        setFilterJobRole(''); setFilterJobId(''); setFilterReqId('');
        setFilterRecruiter(''); setFilterEvantiSpoc(''); setFilterStatus(''); setSearch('');
        setDateResetSignal(x => x + 1);
    };

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Created Tasks" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/team-lead')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', background: '#fff',
                        border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px',
                        fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer'
                    }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Created Tasks</h2>
            </div>

            {/* Create Task form */}
            <form
                onSubmit={handleSubmit}
                style={{
                    background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    padding: '24px', marginBottom: '24px', boxSizing: 'border-box'
                }}
            >
                <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                    Create Task
                </h3>

                {error && (
                    <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Task ID</label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <input style={{ ...lockedInputStyle, flex: 1 }} value={form.display_id} readOnly />
                            <button
                                type="button"
                                onClick={handleRegenerateId}
                                title="Generate a new Task ID"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
                            >
                                <RefreshCw size={14} />
                            </button>
                        </div>
                    </div>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Assign To *</label>
                        <input
                            style={inputStyle}
                            list="recruiter-options"
                            value={form.assigned_to}
                            onChange={handleChange('assigned_to')}
                            placeholder="Type to search..."
                            required
                        />
                        <datalist id="recruiter-options">
                            {recruiters.map(rec => <option key={rec.id} value={rec.name} />)}
                        </datalist>
                    </div>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Assign Date *</label>
                        <input style={inputStyle} type="date" value={form.assigned_date} onChange={handleChange('assigned_date')} min={new Date().toISOString().split('T')[0]} required />
                        {form.job_role && (
                            <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px' }}>Auto-filled from the job's Action Date — still editable</span>
                        )}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Company *</label>
                        <input
                            style={inputStyle}
                            list="company-options"
                            value={form.company}
                            onChange={handleCompanyChange}
                            placeholder={companyOptions.length ? 'Type to search...' : 'No companies with Open jobs'}
                            required
                        />
                        <datalist id="company-options">
                            {companyOptions.map(name => <option key={name} value={name} />)}
                        </datalist>
                    </div>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Job Role *</label>
                        <input
                            style={form.company ? inputStyle : lockedInputStyle}
                            list="job-role-options"
                            value={form.job_role}
                            onChange={handleJobRoleChange}
                            placeholder={form.company ? 'Type to search...' : 'Select a company first'}
                            disabled={!form.company}
                            required
                        />
                        {form.company && blockedJobsForCompany.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>
                                Not available (task not yet Completed): {blockedJobsForCompany.map(j => `${j.job_role} (${blockingTaskFor(j).display_id})`).join(', ')}
                            </div>
                        )}
                        <datalist id="job-role-options">
                            {jobRoleOptionsForCompany.map(j => (
                                <option key={j.job_id} value={j.job_role}>{j.job_id} — {j.req_id || 'no Req ID'}</option>
                            ))}
                        </datalist>
                    </div>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Client SPOC</label>
                        <input style={lockedInputStyle} value={form.client_spoc} readOnly placeholder="Auto-filled from Job Role" />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '16px' }}>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Task To Be Done / Task Instructions *</label>
                        <input style={inputStyle} value={form.title} onChange={handleChange('title')} placeholder="e.g. React Developer" required />
                    </div>

                    <div style={fieldWrapStyle}>
                        <label style={labelStyle}>Keywords / Skills / Special Instructions</label>
                        <input style={inputStyle} value={form.instructions} onChange={handleChange('instructions')} placeholder="Add specific sourcing instructions..." />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px', background: '#2563eb', color: '#fff',
                        border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px',
                        fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1
                    }}
                >
                    <Save size={14} /> {saving ? 'Creating...' : 'Create Task'}
                </button>
            </form>

            {/* Item #3: plain Company filter + the single combined Date Filter (no SPOC, no Global Filters). */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', boxSizing: 'border-box' }}>
                <div style={{ marginBottom: '12px', maxWidth: '260px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' }}>Company Name</label>
                    <select style={filterInputStyle} value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
                        <option value="">All Companies</option>
                        {taskFilterOptions('company').map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div style={{ maxWidth: '680px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' }}>Date (Assigned or Reassigned)</label>
                    <DateFilter key={`created-${dateResetSignal}`} dateField={['assigned_date', 'reassigned_date']} initialValue={dateFilter} onFilterChange={setDateFilter} />
                </div>
            </div>

            {/* Created tasks table */}
            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '20px', overflowX: 'auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                        {user?.role === 'admin' ? 'All Created Tasks' : 'Created Tasks'} ({table.total.toLocaleString()})
                        {user?.role === 'admin' && (
                            <span style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#94a3b8', marginTop: '2px' }}>
                                Tasks created by all Admins and Team Leads
                            </span>
                        )}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <DebouncedInput style={filterInputStyle} list="filter-jobrole-options" value={filterJobRole} onChange={setFilterJobRole} placeholder="Job Role" />
                        <datalist id="filter-jobrole-options">{taskFilterOptions('job_role').map(v => <option key={v} value={v} />)}</datalist>

                        <DebouncedInput style={filterInputStyle} list="filter-jobid-options" value={filterJobId} onChange={setFilterJobId} placeholder="Job ID" />
                        <datalist id="filter-jobid-options">{taskFilterOptions('job_id').map(v => <option key={v} value={v} />)}</datalist>

                        <DebouncedInput style={filterInputStyle} list="filter-reqid-options" value={filterReqId} onChange={setFilterReqId} placeholder="Req ID" />
                        <datalist id="filter-reqid-options">{taskFilterOptions('req_id').map(v => <option key={v} value={v} />)}</datalist>

                        <DebouncedInput style={filterInputStyle} list="filter-recruiter-options" value={filterRecruiter} onChange={setFilterRecruiter} placeholder="Recruiter" />
                        <datalist id="filter-recruiter-options">{taskFilterOptions('assigned_to').map(v => <option key={v} value={v} />)}</datalist>

                        <DebouncedInput style={filterInputStyle} list="filter-evantispoc-options" value={filterEvantiSpoc} onChange={setFilterEvantiSpoc} placeholder="Evanti SPOC" />
                        <datalist id="filter-evantispoc-options">{taskFilterOptions('evanti_spoc').map(v => <option key={v} value={v} />)}</datalist>

                        {hasTaskFilters && (
                            <button type="button" onClick={clearTaskFilters} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '7px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>Loading...</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                                {['Job ID', 'Company', 'Client SPOC', 'Assigned To', 'Job Role', 'Task to be done', 'Assign Date', 'Status',
                                    'Evanti SPOC', 'Reassignment', ...(user?.role === 'admin' ? ['Created By'] : []),].map(h => (
                                        <th key={h} style={{
                                            padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap',
                                            ...(h === 'Job ID' ? { width: '60px', minWidth: '60px', maxWidth: '60px' } : {}),
                                            ...(h === 'Company' ? { width: '75px', minWidth: '75px', maxWidth: '75px' } : {}),
                                            ...(h === 'Client SPOC' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                            ...(h === 'Assigned To' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                            ...(h === 'Job Role' ? { padding: '0px', width: '225px', minWidth: '225px', maxWidth: '225px' } : {}),
                                            ...(h === 'Task to be done' ? { width: '225px', minWidth: '225px', maxWidth: '225px' } : {}),
                                            ...(h === 'Status' ? { textAlign: 'center' } : {}),
                                            ...(h === 'Evanti SPOC' ? { textAlign: 'center' } : {}),
                                            ...(h === 'Reassignment' ? {
                                                width: '150px',
                                                minWidth: '150px',
                                                maxWidth: '150px'
                                            } : {}),
                                        }}>{h}</th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTasks.map((task, snoIdx) => {
                                const colors = statusColors[task.status] || { background: '#e2e8f0', color: '#334155' };
                                return (
                                    <tr key={task.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px', color: '#64748b', fontWeight: '600' }}>{(table.page - 1) * table.pageSize + snoIdx + 1}</td>
                                        {/* <td style={{ padding: '8px', fontWeight: '700', color: '#2563eb' }}>{task.display_id}</td> */}
                                        <td style={{ padding: '8px' }}>{task.job_id || '-'}</td>
                                        <td style={{ padding: '8px' }}>{task.company}</td>
                                        {/* <td style={{ padding: '8px' }}>{task.req_id || '-'}</td> */}
                                        <td style={{ padding: '8px', width: '100px', minWidth: '100px', maxWidth: '100px' }}>{task.client_spoc || '-'}</td>
                                        <td style={{ padding: '8px' }}>{task.assigned_to}</td>
                                        <td style={{ width: '225px', minWidth: '225px', maxWidth: '225px' }}>{task.job_role || '-'}</td>
                                        <td style={{ padding: '8px', width: '225px', minWidth: '225px', maxWidth: '225px' }}>{task.title}</td>
                                        <td style={{ padding: '8px' }}>{formatDisplayDate(task.assigned_date)}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <span style={{ ...colors, padding: '2px 8px', borderRadius: '10px', fontWeight: '700', fontSize: '11px' }}>
                                                {task.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>{task.evanti_spoc || '-'}</td>
                                        <td style={{ padding: '8px', fontSize: '11px', color: '#92400e' }}>
                                            {/* Item #2: same display-only format as the Kanban card, display-only — no schema change. */}
                                            {task.reassignment_count > 0
                                                ? `Reassigned ${task.reassignment_count}x${task.reassigned_by ? ` — by ${task.reassigned_by}` : ''}${task.reassigned_date ? ` on ${formatDisplayDate(task.reassigned_date)}` : ''}${task.reassigned_to ? ` to: ${task.reassigned_to}` : ''}`
                                                : '-'}
                                        </td>
                                        {user?.role === 'admin' && <td style={{ padding: '8px' }}>{task.assigned_by || '-'}</td>}
                                    </tr>
                                );
                            })}
                            {filteredTasks.length === 0 && (
                                <tr>
                                    <td colSpan={user?.role === 'admin' ? 14 : 13} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>
                                        {hasTaskFilters ? 'No tasks match these filters.' : 'No tasks found for today. Change the date filter to see earlier tasks.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
                <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                    onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="tasks" />
            </div>
        </div>
    );
};

export default CreatedTasksList;